/**
 * ArticleBriefContext - Simplified View State Management
 *
 * This context now ONLY handles UI/view state. All server state (briefs data,
 * loading, errors) is managed by React Query hooks in:
 * - features/article-brief/api/articleBriefQueries.ts
 *
 * This prevents state management complexity and leverages React Query's
 * automatic caching, refetching, and error handling.
 */

import type React from 'react';
import { createContext, useCallback, useContext, useReducer, useMemo } from 'react';
import type {
  ArticleBrief,
  FilterOptions,
  ValidationError,
  ArticleBriefFormData,
} from '../types/article-brief';

// State types - ONLY view state, no server state
interface AppState {
  currentBrief: ArticleBrief | null; // Currently selected brief for navigation
  viewState: {
    mode: 'list' | 'form' | 'detail' | 'dashboard' | 'outline';
    searchQuery: string;
    filters: FilterOptions;
    sortBy: 'name' | 'title' | 'createdAt' | 'updatedAt';
    sortDirection: 'asc' | 'desc';
  };
}

// Action types - Simplified to view state only
type AppAction =
  | { type: 'SET_CURRENT_BRIEF'; payload: ArticleBrief | null }
  | { type: 'SET_VIEW_MODE'; payload: AppState['viewState']['mode'] }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_FILTERS'; payload: FilterOptions }
  | { type: 'SET_SORT'; payload: { sortBy: 'name' | 'title' | 'createdAt' | 'updatedAt'; sortDirection: 'asc' | 'desc' } };

// Context type
interface ArticleBriefContextType {
  state: AppState;
  actions: {
    setViewMode: (mode: AppState['viewState']['mode']) => void;
    setCurrentBrief: (brief: ArticleBrief | null) => void;
    setSearchQuery: (query: string) => void;
    setFilters: (filters: FilterOptions) => void;
    setSort: (sortBy: 'name' | 'title' | 'createdAt' | 'updatedAt', sortDirection: 'asc' | 'desc') => void;
    validateBrief: (data: ArticleBriefFormData) => Promise<{ isValid: boolean; errors: ValidationError[] }>;
  };
  dispatch: React.Dispatch<AppAction>;
}

// Initial state
const initialState: AppState = {
  currentBrief: null,
  viewState: {
    mode: 'list',
    searchQuery: '',
    filters: {},
    sortBy: 'updatedAt',
    sortDirection: 'desc',
  },
};

// Reducer - Simplified to view state only
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_CURRENT_BRIEF':
      return {
        ...state,
        currentBrief: action.payload,
      };

    case 'SET_VIEW_MODE':
      return {
        ...state,
        viewState: {
          ...state.viewState,
          mode: action.payload,
        },
      };

    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        viewState: {
          ...state.viewState,
          searchQuery: action.payload,
        },
      };

    case 'SET_FILTERS':
      return {
        ...state,
        viewState: {
          ...state.viewState,
          filters: action.payload,
        },
      };

    case 'SET_SORT':
      return {
        ...state,
        viewState: {
          ...state.viewState,
          sortBy: action.payload.sortBy,
          sortDirection: action.payload.sortDirection,
        },
      };

    default:
      return state;
  }
};

// Context
const ArticleBriefContext = createContext<ArticleBriefContextType | undefined>(undefined);

// Provider component
export const ArticleBriefProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  /**
   * Client-side validation for article brief forms
   * This stays in context since it's not server state
   */
  const validateBrief = useCallback(async (
    data: ArticleBriefFormData
  ): Promise<{ isValid: boolean; errors: ValidationError[] }> => {
    const errors: ValidationError[] = [];

    if (!data.name || data.name.length < 1) {
      errors.push({ field: 'name', message: 'Name is required', severity: 'error' });
    }

    if (!data.title || data.title.length < 1) {
      errors.push({ field: 'title', message: 'Title is required', severity: 'error' });
    }

    if (!data.task || data.task.length < 10) {
      errors.push({
        field: 'task',
        message: 'Task must be at least 10 characters',
        severity: 'error',
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, []);

  const actions = useMemo(
    () => ({
      setViewMode: (mode: AppState['viewState']['mode']) =>
        dispatch({ type: 'SET_VIEW_MODE', payload: mode }),
      setCurrentBrief: (brief: ArticleBrief | null) =>
        dispatch({ type: 'SET_CURRENT_BRIEF', payload: brief }),
      setSearchQuery: (query: string) => dispatch({ type: 'SET_SEARCH_QUERY', payload: query }),
      setFilters: (filters: FilterOptions) => dispatch({ type: 'SET_FILTERS', payload: filters }),
      setSort: (
        sortBy: 'name' | 'title' | 'createdAt' | 'updatedAt',
        sortDirection: 'asc' | 'desc'
      ) => dispatch({ type: 'SET_SORT', payload: { sortBy, sortDirection } }),
      validateBrief,
    }),
    [validateBrief]
  );

  const contextValue = useMemo(
    () => ({
      state,
      actions,
      dispatch,
    }),
    [state, actions]
  );

  return (
    <ArticleBriefContext.Provider value={contextValue}>{children}</ArticleBriefContext.Provider>
  );
};

// Hook to use the context
export const useArticleBrief = () => {
  const context = useContext(ArticleBriefContext);
  if (context === undefined) {
    throw new Error('useArticleBrief must be used within an ArticleBriefProvider');
  }
  return context;
};

// Backward compatibility exports
/** @deprecated Use ArticleBriefProvider instead */
export const ResearchDefinitionProvider = ArticleBriefProvider;

/** @deprecated Use useArticleBrief instead */
export const useResearchDefinition = useArticleBrief;
